import { useMemo } from "react";
import { useLingui } from "@lingui/react";
import { ShopType, SingleProduct } from "src/types/graphql.d";
import { getI18nRoute } from "src/utils/i18nRoutes";
import { Locale } from "src/lib/Intl";
import { msg } from "@lingui/macro";
import { shopTypeTranslation } from "src/translations/shopTypeTranslation";
import { useShopTypeContext } from "src/hooks/context/use-shop-type-context";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import { Breadcrumb, BreadcrumbItem } from "src/organisms/Breadcrumb";

type ProductBreadcrumbsProps = {
  product?: SingleProduct;
  genderId: string;
  locale: Locale;
};

function ProductBreadcrumbs({
  product,
  genderId,
  locale,
}: ProductBreadcrumbsProps): JSX.Element {
  const { currentShopType } = useShopTypeContext();
  const localeShopType = useMemo(
    () => localeFix({ gender: currentShopType }),
    [currentShopType]
  );

  const kidsSubGender =
    product && (product?.kids_sub_categories?.[0]?.slug as string);
  const gender = kidsSubGender ? ShopType.Kids : localeShopType;
  const productCategorySlugHref = kidsSubGender
    ? getI18nRoute({ route: `/${localeShopType}/${kidsSubGender}`, locale })
    : `${getI18nRoute({
        route: `/${gender.toLowerCase()}`,
        locale,
      })}/categories`;
  const { _ } = useLingui();

  const items: BreadcrumbItem[] = [
    {
      title: _(msg`Home`),
      slug: getI18nRoute({ route: `/${localeShopType}`, locale }),
    },
    {
      title: _(shopTypeTranslation[gender]),
      slug: productCategorySlugHref,
    },
    ...(product
      ? [
          {
            title: product.brand.name,
            slug: `${getI18nRoute({ route: `/brands`, locale })}/${
              product.brand.slug
            }${getI18nRoute({ route: `/${localeShopType}`, locale })}`,
          },
          {
            title: product.name,
          },
        ]
      : []),
  ];

  return <Breadcrumb data={items} />;
}

export default ProductBreadcrumbs;
